import { setCurrentLocaleCode } from "@mongez/localization";
import { LinksFunction, LoaderFunction } from "@remix-run/node";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
  useRouteLoaderData,
} from "@remix-run/react";
import { AppErrorBoundary, ErrorPageAdapter } from "core/logs/components";
import { useRouteTracker } from "core/logs/hooks";
import { withErrorLogging } from "core/logs/utils";
import { commonLayoutAtom, Settings, settingsAtom } from "core/shared";
import { User, userAtom } from "core/user";
import { handleRedirects, publicRoutes } from "framework/config";
import toast, { ToastBar, Toaster } from "react-hot-toast";
import "~/config/translations/resource";
import styles from "./app.css?url";

import { CommonLayout } from "core/shared/types";
import { getCached } from "framework/config/cache";
import { X } from "lucide-react";
import { useEffect, useRef } from "react";
import websiteLogo from "~/assets/images/footer/footer-logo.png";
import websiteIcon from "~/assets/images/icon.ico";
import { ErrorPage } from "~/modules/shared/components";

type LoaderData = {
  lang: string;
  user: User;
  settings: Settings;
  commonLayout: CommonLayout;
  url: string;
  domainUrl: string;
};

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
  { rel: "stylesheet", href: styles },
  { rel: "icon", href: websiteIcon },
];

export const loader: LoaderFunction = publicRoutes(
  async ({ request, params, user, domain, success, http }) => {
    const result = handleRedirects({ request, params });

    if (result instanceof Response) {
      return result;
    }

    // if (!domain.includes("localhost") && !domain.includes("beta")) {
    //   return catchErrors({
    //     response: {
    //       status: 503,
    //     },
    //   });
    // }

    const { lang = "en" } = result;

    const [{ settings }, { layout }] = await Promise.all([
      getCached(`${domain}.settings.${lang}`, () => http.get("/settings")),
      getCached(`${domain}.layout.${lang}`, () => http.get("/layouts/common")),
    ]);

    return success({
      lang,
      user,
      settings,
      commonLayout: layout,
      url: request.url,
      domainUrl: `https://${domain}`,
    });
  },
);

export function Layout({ children }: { children: React.ReactNode }) {
  const data = useRouteLoaderData<LoaderData>("root");

  const domainUrl = data?.domainUrl!;

  return (
    <html lang={data?.lang} dir={data?.lang === "ar" ? "rtl" : "ltr"}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta property="og:image" content={`${domainUrl}${websiteLogo}`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:url"
          content={data?.url?.replace("http", "https") || domainUrl}
        />
        <meta property="og:type" content="website" />
        <meta property="fb:app_id" content="1348740876729152" />
        <Meta />
        <Links />
        <link
          rel="canonical"
          href={data?.url?.replace("http", "https") || domainUrl}
        />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              // Initialize Google Analytics with consent
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              
              // Set default consent to denied
              gtag('consent', 'default', {
                'analytics_storage': 'denied',
                'ad_storage': 'denied',
                'functionality_storage': 'denied',
                'personalization_storage': 'denied',
                'security_storage': 'granted'
              });
              
              // Check for existing consent
              const savedConsent = localStorage.getItem('cookie_consent');
              if (savedConsent) {
                try {
                  const consent = JSON.parse(savedConsent);
                  if (consent.analytics) {
                    gtag('consent', 'update', {
                      'analytics_storage': 'granted'
                    });
                  }
                  if (consent.marketing) {
                    gtag('consent', 'update', {
                      'ad_storage': 'granted',
                      'personalization_storage': 'granted'
                    });
                  }
                  if (consent.functional) {
                    gtag('consent', 'update', {
                      'functionality_storage': 'granted'
                    });
                  }
                } catch (error) {
                  console.error('Error parsing cookie consent:', error);
                }
              }
              
              // Load Google Analytics script only if analytics consent is granted
              function loadGoogleAnalytics() {
                const script = document.createElement('script');
                script.async = true;
                script.src = 'https://www.googletagmanager.com/gtag/js?id=G-M50MW7620N';
                document.head.appendChild(script);
                
                gtag('config', 'G-M50MW7620N');
              }
              
              // Load analytics if consent is already granted
              if (savedConsent) {
                try {
                  const consent = JSON.parse(savedConsent);
                  if (consent.analytics) {
                    loadGoogleAnalytics();
                  }
                } catch (error) {
                  console.error('Error parsing cookie consent:', error);
                }
              }
              
              // Make loadGoogleAnalytics available globally for consent updates
              window.loadGoogleAnalytics = loadGoogleAnalytics;
            `,
          }}
        />
      </head>
      <body className="font-playfair">
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

// Create AppErrorBoundary with error logging
const AppErrorBoundaryWithLogging = withErrorLogging(AppErrorBoundary);

export default function App() {
  const { lang, user, settings, commonLayout } = useLoaderData<LoaderData>();

  // Seed atoms silently on the first render so children read loader data
  // immediately without triggering setState in other components during render.
  const isFirstRender = useRef(true);

  if (isFirstRender.current) {
    userAtom.silentUpdate(user);
    settingsAtom.silentUpdate(settings);
    commonLayoutAtom.silentUpdate(commonLayout);
  }

  // Notify subscribers when loader data changes after revalidation.
  useEffect(() => {
    isFirstRender.current = false;
    userAtom.update(user);
    settingsAtom.update(settings);
    commonLayoutAtom.update(commonLayout);
  }, [user, settings, commonLayout]);

  setCurrentLocaleCode(lang || "en");

  // Initialize route tracking for error logging
  useRouteTracker();

  return (
    <AppErrorBoundaryWithLogging
      fallback={(error, errorInfo) => (
        <ErrorPageAdapter
          error={error}
          errorInfo={errorInfo}
          ErrorPageComponent={ErrorPage}
        />
      )}
    >
      <Outlet />
      <Toaster position="top-right" toastOptions={{ duration: 5000 }}>
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => (
              <div className="flex items-center gap-3 py-1">
                <div className="shrink-0">{icon}</div>
                <div className="text-sm text-gray-800">{message}</div>
                {t.type !== "loading" && (
                  <button
                    onClick={() => toast.dismiss(t.id)}
                    className="ml-2 inline-flex shrink-0 cursor-pointer items-center justify-center rounded-full p-1 text-gray-400 transition-all duration-200 hover:bg-gray-100 hover:text-gray-900"
                    aria-label="Close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}
          </ToastBar>
        )}
      </Toaster>
    </AppErrorBoundaryWithLogging>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();

  console.log(error, "error");
  const status =
    error && typeof error === "object" && "status" in error
      ? (error as { status: number }).status
      : undefined;

  return <ErrorPage error={error} status={status} />;
}
